/**
 * Pure helpers that turn the create form's raw `formData` into the payload
 * handed to `config.onSubmit`. No React, no I/O — exercised directly by
 * tests and by `CrmCreateForm` at submit time.
 */

import {
  type DraftEntity,
  mergeOverwriteTargets,
} from '@/lib/crm/core/draftEntities';

/**
 * Check if a form field value is considered "empty" for auto-fill purposes.
 * Returns true if value is null, undefined, or an empty array.
 */
export function isFieldEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
}

/**
 * Check if a quantity value is incomplete — has a unit but no amount.
 * This happens when the form pre-fills the currency dropdown (e.g. { unit: "USD" })
 * but the user doesn't enter an amount. Unlike PhoneInput (which emits undefined
 * when empty), the currency field always carries a unit from form initialization,
 * so we need to detect and strip these partial objects before submission.
 */
export function isQuantityEmpty(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    'unit' in obj &&
    !(
      'amount' in obj &&
      obj.amount !== null &&
      obj.amount !== '' &&
      obj.amount !== undefined
    )
  );
}

export interface AssociationEntry {
  target_entity_id: string;
  association_type_name: string;
}

export interface BuildSubmitPayloadArgs {
  formData: Record<string, unknown>;
  /** Names of `property` fields — cleaned and forwarded as properties. */
  propertyNames: ReadonlySet<string>;
  /** Names of `custom` fields — forwarded verbatim, empties included. */
  customNames: ReadonlySet<string>;
  /** Association type names rendered by the form. */
  associationTypeNames: ReadonlySet<string>;
  /** Org default currency, used to repair a quantity whose unit never loaded. */
  defaultCurrency: string | undefined;
  /** Resolves the drafts referenced by the submitted associations. */
  collectReferencedDrafts: (
    associations: ReadonlyArray<{ target_entity_id: string }>
  ) => DraftEntity[];
  /** `conflictKey(entity_id, association_type_name)` values the user acknowledged. */
  ackedConflictKeys: ReadonlySet<string>;
}

/**
 * Separate properties from associations, strip empty values, fold in
 * referenced drafts and acknowledged overwrite targets.
 *
 * Keys that belong to none of the three field sets are dropped: they are
 * internal bookkeeping, never user-facing values.
 */
export function buildSubmitPayload({
  formData,
  propertyNames,
  customNames,
  associationTypeNames,
  defaultCurrency,
  collectReferencedDrafts,
  ackedConflictKeys,
}: BuildSubmitPayloadArgs): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  const associations: AssociationEntry[] = [];

  for (const [key, value] of Object.entries(formData)) {
    if (customNames.has(key)) {
      properties[key] = value;
      continue;
    }
    if (value === '' || value === null || value === undefined) {
      continue;
    }
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    if (isQuantityEmpty(value)) {
      continue;
    }

    if (associationTypeNames.has(key)) {
      if (Array.isArray(value)) {
        for (const entityId of value as string[]) {
          associations.push({
            target_entity_id: entityId,
            association_type_name: key,
          });
        }
      } else {
        associations.push({
          target_entity_id: value as string,
          association_type_name: key,
        });
      }
    } else if (propertyNames.has(key)) {
      // Belt-and-suspenders fix for the quantity-without-unit race
      // (currentOrg.defaultCurrency loading after the user types an
      // amount): if a quantity has a valid amount but an empty unit,
      // backfill the unit from the org's defaultCurrency before
      // submitting. Without this, the backend rejects the event with
      //   "'<Property>' unit must not be empty or whitespace"
      // and the form stays open with the inline error.
      //
      // The component-level effects in CurrencyInput.tsx try to repair
      // value.unit when units load, but the parent's re-render path is
      // not always observable in time on a cold form open. This guard
      // runs at the actual submit boundary so the request can never
      // ship a partial quantity regardless of which intermediate
      // effect won the race.
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        'amount' in value &&
        'unit' in value &&
        (value as { unit?: unknown }).unit === '' &&
        defaultCurrency
      ) {
        properties[key] = { ...(value as object), unit: defaultCurrency };
      } else {
        properties[key] = value;
      }
    }
  }

  const payload: Record<string, unknown> = { ...properties };
  if (associations.length > 0) {
    payload.associations = associations;
  }
  const referencedDrafts = collectReferencedDrafts(associations);
  if (referencedDrafts.length > 0) {
    payload.draft_entities = referencedDrafts;
  }
  // Include acknowledged overwrite targets so the backend can remove
  // conflicting existing associations before adding the new ones.
  // Authorization is scoped per (entity_id, association_type_name) pair
  // so acknowledging one conflict cannot cause unintended removals on a
  // different association type for the same entity. A referenced draft
  // whose association overwrites an existing single-cardinality record
  // carries its own acknowledgement; overwrite_targets is batch-scoped, so
  // those must be merged into the top-level list here — otherwise the
  // folded draft associationAdded ships unauthorized and the backend
  // rejects the whole batch with a cardinality violation.
  const overwriteTargets = mergeOverwriteTargets(
    Array.from(ackedConflictKeys).map(k => {
      const [entity_id, association_type_name] = k.split('|');
      return { entity_id, association_type_name };
    }),
    referencedDrafts.flatMap(d => d.overwrite_targets ?? [])
  );
  if (overwriteTargets.length > 0) {
    payload.overwrite_targets = overwriteTargets;
  }
  return payload;
}
